import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { CtaBlock } from "../components/CtaBlock";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Img } from "../components/Img";

const CATEGORIES = ["All", "Collision", "Frame", "Paint", "Performance", "Luxury"];

const PHOTOS = [
  { name: "collision-repair-carrollton-tx", cat: "Collision", alt: "Certified collision repair on a luxury vehicle at Westway Collision Center in Carrollton, TX" },
  { name: "volkswagen-collision-repair-carrollton", cat: "Collision", alt: "Volkswagen mid-collision-repair at Westway Collision Center in Carrollton, TX" },
  { name: "westway-collision-shop-interior", cat: "Collision", alt: "Collision repair in progress inside the Westway Collision Center shop in Carrollton, TX" },
  { name: "westway-auto-repair-bay", cat: "Collision", alt: "Repair bay with vehicles in progress at Westway Collision Center in Carrollton, TX" },
  { name: "frame-straightening-carrollton-tx", cat: "Frame", alt: "Frame straightening on the computerized frame machine at Westway Collision Center in Carrollton, TX" },
  { name: "frame-chassis-repair", cat: "Frame", alt: "Bare chassis during structural frame repair at Westway Collision Center in Carrollton, TX" },
  { name: "camaro-wheel-alignment-carrollton", cat: "Frame", alt: "Chevrolet Camaro precision wheel alignment after structural work in Carrollton, TX" },
  { name: "auto-body-paint-carrollton-tx", cat: "Paint", alt: "Auto body and paint refinishing at Westway Collision Center in Carrollton, TX" },
  { name: "lamborghini-body-panel-repair-dfw", cat: "Paint", alt: "Lamborghini body panel paint repair at Westway Collision Center serving Carrollton, TX and DFW" },
  { name: "performance-tuning-carrollton-tx", cat: "Performance", alt: "Performance parts prepared for tuning work at Westway Collision Center in Carrollton, TX" },
  { name: "lamborghini-huracan-performance-tuning", cat: "Performance", alt: "Lamborghini Huracan performance tuning at Westway Collision Center in Carrollton, TX" },
  { name: "dodge-ram-performance-upgrade", cat: "Performance", alt: "Dodge Ram performance upgrade at Westway Collision Center in Carrollton, TX" },
  { name: "gtr-hunter-alignment", cat: "Performance", alt: "Nissan GT-R on the Hunter rack with measuring targets in Carrollton, TX" },
  { name: "ford-raptor-diagnostics", cat: "Performance", alt: "Ford Raptor receiving engine diagnostics at Westway Collision Center in Carrollton, TX" },
  { name: "mercedes-g-wagon-westway-collision", cat: "Luxury", alt: "Mercedes G-Wagon serviced at Westway Collision Center in Carrollton, TX" },
  { name: "rolls-royce-luxury-detailing-carrollton", cat: "Luxury", alt: "Rolls-Royce luxury detailing at Westway Collision Center in Carrollton, TX" },
  { name: "rolls-royce-spectre-service-bay", cat: "Luxury", alt: "Rolls-Royce Spectre in the service bay at Westway Collision Center in Carrollton, TX" },
  { name: "corvette-repair-after-carrollton", cat: "Luxury", alt: "Corvette finished after repair at Westway Collision Center in Carrollton, TX" },
  { name: "westway-collision-shop-carrollton-tx", cat: "Collision", alt: "Westway Collision Center shop floor with luxury vehicles in Carrollton, TX" },
];

export default function OurWork() {
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(-1);
  const photos = cat === "All" ? PHOTOS : PHOTOS.filter((p) => p.cat === cat);

  const step = useCallback(
    (d) => setLightbox((i) => (i + d + photos.length) % photos.length),
    [photos.length]
  );

  useEffect(() => {
    if (lightbox < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(-1);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, step]);

  return (
    <div data-testid="our-work-page">
      <Seo
        title="Our Work — Real Repairs Gallery | Westway Collision Center"
        description="Real collision, frame, paint, performance & luxury repairs by Westway Collision Center in Carrollton, TX. Lamborghinis to Rolls-Royce. (214) 888-2886."
        path="/our-work"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Our Work", path: "/our-work" }])]}
      />

      <section className="relative pt-32 lg:pt-40 pb-12 overflow-hidden">
        <div className="absolute inset-0 surface-weave opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Our Work", path: "/our-work" }]} />
          <Reveal>
            <Eyebrow>Every Photo Shot In-House</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] text-balance max-w-4xl">
              Our Work — Real Repairs in Carrollton, TX
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              No stock photography anywhere on this page — every image below was shot inside our
              Carrollton facility, on real customer vehicles, by our own team.
            </p>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="py-14 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap gap-3 mb-10" y={16}>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${c.toLowerCase()}`}
                onClick={() => {
                  setCat(c);
                  setLightbox(-1);
                }}
                aria-pressed={cat === c}
                className={`rounded-full border px-5 py-2.5 font-mono text-[11px] tracking-[0.16em] uppercase transition-all duration-300 ${
                  cat === c
                    ? "border-accent bg-accentdeep text-white font-semibold"
                    : "border-white/15 text-mute hover:border-accent/50 hover:text-accent"
                }`}
              >
                {c}
              </button>
            ))}
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {photos.map((p, i) => (
              <Reveal key={p.name} delay={(i % 3) * 0.05}>
                <button
                  data-testid={`gallery-item-${p.name}`}
                  onClick={() => setLightbox(i)}
                  className="group relative block w-full overflow-hidden rounded-2xl border border-white/[0.08] transition-all duration-500 hover:border-accent/40 hover:-translate-y-1 text-left"
                  aria-label={`Enlarge photo: ${p.alt}`}
                >
                  <Img name={p.name} alt={p.alt} className="block mx-auto h-60 sm:h-64 w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" />
                  <span className="absolute bottom-3 left-3 rounded-full border border-accent/30 bg-ink/75 backdrop-blur-md px-3 py-1 font-mono text-[9px] tracking-[0.2em] uppercase text-accent">
                    {p.cat}
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox >= 0 && photos[lightbox] && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/95 backdrop-blur-xl p-4 sm:p-10"
            onClick={() => setLightbox(-1)}
            role="dialog"
            aria-modal="true"
            aria-label="Photo lightbox"
          >
            <button data-testid="lightbox-close" onClick={() => setLightbox(-1)} aria-label="Close photo" className="absolute top-5 right-5 rounded-full border border-white/20 bg-surface p-3 text-white hover:border-accent/60 hover:text-accent transition-colors">
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev"
              aria-label="Previous photo"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-surface p-3 text-white hover:border-accent/60 hover:text-accent transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <motion.figure
              key={photos[lightbox].name}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <Img name={photos[lightbox].name} alt={photos[lightbox].alt} eager className="max-h-[75vh] w-full object-contain rounded-xl border border-white/10 bg-surface" sizes="100vw" />
              <figcaption className="mt-4 text-center text-sm text-slate-400">{photos[lightbox].alt}</figcaption>
            </motion.figure>
            <button
              data-testid="lightbox-next"
              aria-label="Next photo"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-surface p-3 text-white hover:border-accent/60 hover:text-accent transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CtaBlock heading="Want Yours To Look This Good?" sub="Free estimates, insurance handled, lifetime warranty in writing." />
        </div>
      </section>
    </div>
  );
}
